/**
 * The makeup of a section to be used by our SectionRenderer
 *
 * @typedef {Object} Section
 * @property {String} id - The ID of the section. This will be the filename of the section
 * @property {String} section - The section file name used in the sections API
 * @property {String} selector - The CSS selector which contains the sections content
 * @property {String} html - This is the HTML returned by the section rendering API
 */
window.BAO.utils.sectionRenderer = new (class SectionRenderer {
  /**
   * Render a section returned from the section rendering API
   *
   * @param {Section} section - The makeup of the section we want to render
   *
   * @returns {void}
   */
  render(section) {
    this.validate(section);

    const baseElement = this.findSectionRoot(section);

    if (!baseElement) {
      console.info(
        `Tried to render ${section.id} but no element with id="${section.id}" or data-section-type="${section.section || section.id}" exists`
      );

      return;
    }

    if (!section.html) {
      baseElement.innerHTML = "";
      return;
    }

    const innerHtml = this.getInnerHtml(section.html, section.selector);

    if (innerHtml === null) {
      return;
    }

    const target =
      baseElement.querySelector(section.selector) || baseElement;

    if (target === baseElement) {
      baseElement.innerHTML = innerHtml;
      return;
    }

    target.innerHTML = innerHtml;
  }

  /**
   * Render multiple sections returned from the section rendering API
   *
   * @param {Section[]} sections - The sections we want to render
   *
   * @returns {void}
   */
  renderMultiple(sections) {
    sections.forEach((section) => {
      try {
        this.render(section);
      } catch (error) {
        console.error(`Failed to render section ${section.id}`, error);
      }
    });
  }

  findSectionRoot(section) {
    const sectionKey = section.section || section.id;

    return (
      document.getElementById(section.id) ||
      document.querySelector(`[data-section-type="${sectionKey}"]`)
    );
  }

  /**
   * Grab the HTML that is inside the section otherwise we end up with section inception
   *
   * @param {String} html - The section HTML returned from the section rendering API
   * @param {String} selector - The CSS selector which contains the sections content
   *
   * @returns {String|null}
   */
  getInnerHtml(html, selector) {
    if (!html) {
      return null;
    }

    const doc = new DOMParser().parseFromString(html, "text/html");
    const element =
      doc.querySelector(selector) ||
      doc.body.firstElementChild ||
      doc.body;

    if (!element) {
      return null;
    }

    return element.innerHTML;
  }

  /**
   * Render a section returned from the section rendering API
   *
   * @param {Section} section - The makeup of the section we want to render
   *
   * @returns {Boolean}
   */
  validate(section) {
    const isValid = this.expectedKeys.every((key) => key in section);

    if (!isValid) {
      throw new Error(
        `section is missing these keys: "${this.expectedKeys
          .filter((k) => !(k in section))
          .join(", ")}"`
      );
    }

    return isValid;
  }

  get expectedKeys() {
    return ["id", "selector", "html"];
  }

  get cartSections() {
    return [
      {
        id: "cart-items",
        section: "cart-items",
        selector: ".shopify-section",
      },
      {
        id: "cart-upsell",
        section: "cart-upsell",
        selector: ".shopify-section",
      },
      {
        id: "cart-summary",
        section: "cart-summary",
        selector: ".shopify-section",
      },
      {
        id: "push-cart-godark",
        section: "push-cart-godark",
        selector: ".shopify-section",
      },
    ];
  }

  get pushCartSections() {
    return [
      {
        id: "push-cart-delivery",
        section: "push-cart-delivery",
        selector: "#push-cart-delivery",
      },
      {
        id: "push-cart-items",
        section: "push-cart-items",
        selector: "#push-cart-items",
      },
      {
        id: "push-cart-multibuy",
        section: "push-cart-multibuy",
        selector: "#push-cart-multibuy",
      },
      {
        id: "push-cart-footer",
        section: "push-cart-footer",
        selector: "#push-cart-footer",
      },
      {
        id: "gwp-cart-bogof",
        section: "gwp-cart-bogof",
        selector: "#gwp-cart-bogof",
      },
      {
        id: "gwp-cart-threshold",
        section: "gwp-cart-threshold",
        selector: "#gwp-cart-threshold",
      },
    ];
  }

  /** Sections needed for an immediate cart drawer update */
  get pushCartPrioritySectionNames() {
    return [
      "push-cart-items",
      "push-cart-footer",
      "push-cart-multibuy",
    ];
  }

  /** Heavier sections fetched after the drawer has updated */
  get pushCartDeferredSectionNames() {
    return [
      "push-cart-delivery",
      "gwp-cart-bogof",
      "gwp-cart-threshold",
    ];
  }

  /** Cart-context URL for section rendering requests */
  get pushCartSectionsUrl() {
    return window.routes?.cart_url || "/cart";
  }

  /**
   * Split requested sections into a fast cart API batch and a deferred batch.
   *
   * @param {string[]} sectionNames
   *
   * @returns {{ apiSections: string[], deferredSections: string[] }}
   */
  partitionCartSections(sectionNames) {
    const priority = this.pushCartPrioritySectionNames;

    return {
      apiSections: sectionNames.filter((name) => priority.includes(name)),
      deferredSections: sectionNames.filter((name) => !priority.includes(name)),
    };
  }

  /**
   * Shopify cart APIs accept at most 5 sections per request.
   *
   * @param {string[]} sectionNames
   * @param {string} [sectionsUrl]
   *
   * @returns {Promise<object>}
   */
  async fetchSections(
    sectionNames,
    sectionsUrl = this.pushCartSectionsUrl
  ) {
    if (!sectionNames.length) {
      return {};
    }

    const sections = {};
    const batchSize = 5;

    const batches = [];
    for (let i = 0; i < sectionNames.length; i += batchSize) {
      batches.push(sectionNames.slice(i, i + batchSize));
    }

    const responses = await Promise.all(
      batches.map((batch) => {
        const url = new URL(sectionsUrl, window.location.origin);
        url.searchParams.set("sections", batch.join(","));
        url.searchParams.set("_", Date.now().toString());

        return fetch(url, { cache: "no-store" }).then((response) =>
          response.json()
        );
      })
    );

    responses.forEach((data) => {
      if (!data || typeof data !== "object") {
        return;
      }

      if (data.sections && typeof data.sections === "object") {
        Object.assign(sections, data.sections);
        return;
      }

      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === "string") {
          sections[key] = value;
        }
      });
    });

    return sections;
  }
})();
